import { useContext, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { OutcomeFormPlaceOfSafety } from "@thalamos/aspire-common";

import { Button, renderErrorToast } from "~/components/design-system/index.js";
import { PopupDialogTitle } from "~/components/design-system/PopupDialog/PopupDialogTitle.js";
import type { SelectedTrust } from "~/components/form/TrustPicker.js";
import { TrustPicker } from "~/components/form/TrustPicker.js";
import { ManagedLocationsContext } from "~/Contexts.js";

/**
 * A place of safety from the incident (Vision) data.
 * These are the POS records shared from the police officer's incident.
 */
interface IncidentPlaceOfSafety {
  id: string;
  eMHACuratedPlaceOfSafetyId?: string;
  name: string;
  address: string;
  postalCode: string;
  isActive: boolean;
}

/**
 * Unified POS option shown in the single typeahead.
 * May originate from an incident POS, a curated POS, or both.
 */
interface UnifiedPosOption {
  /** Unique key for deduplication. Curated POS ID if available, otherwise incident POS id. */
  key: string;
  /** Display name. */
  name: string;
  /** Address line. */
  address: string;
  /** Postal code. */
  postalCode: string;
  /** True if this POS appears on the current incident. */
  isOnIncident: boolean;
  /** True if an outcome form (in-progress or completed) already exists for this POS. */
  hasExistingForm: boolean;
  /** The curated POS ID if this is a curated POS. */
  curatedPlaceOfSafetyId?: string;
  /** Original incident POS entry (if sourced from incident). */
  incidentPos?: IncidentPlaceOfSafety;
}

type PosSelectionMode = "search" | "manual";

interface OutcomeFormPosSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (placeOfSafety: OutcomeFormPlaceOfSafety) => void;
  /** Places of safety from the Vision incident. */
  incidentPlacesOfSafety: IncidentPlaceOfSafety[];
  /** Loading state while creating/claiming the outcome form. */
  isSubmitting?: boolean;
  /** POS entries that already have an outcome form (in-progress or completed). */
  existingOutcomeFormPosEntries?: OutcomeFormPlaceOfSafety[];
}

/**
 * Modal for selecting a place of safety when creating a new outcome form.
 * Provides a single typeahead combining incident and curated POS with visual
 * indicators, plus a manual entry fallback.
 */
export function OutcomeFormPosSelectionModal({
  open,
  onClose,
  onSelect,
  incidentPlacesOfSafety,
  isSubmitting = false,
  existingOutcomeFormPosEntries = [],
}: OutcomeFormPosSelectionModalProps) {
  const { t } = useTranslation();
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const [mode, setMode] = useState<PosSelectionMode>("search");

  // Unified POS selection
  const [selectedPos, setSelectedPos] = useState<UnifiedPosOption | null>(null);

  // Manual entry
  const [manualName, setManualName] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [manualPostalCode, setManualPostalCode] = useState("");
  const [manualTrust, setManualTrust] = useState<SelectedTrust | null>(null);

  const curatedPlacesOfSafety = useMemo(
    () =>
      [...managedLocationsContext.curatedPlacesOfSafety].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [managedLocationsContext.curatedPlacesOfSafety],
  );

  /** Check if a curated POS ID already has an outcome form. */
  const isCuratedPosAlreadyUsed = (curatedPosId: string) =>
    existingOutcomeFormPosEntries.some(
      (e) => e.curatedPlaceOfSafetyId === curatedPosId,
    );

  /** Check if a manual POS name already has an outcome form. */
  const isManualPosAlreadyUsed = (posName: string) =>
    existingOutcomeFormPosEntries.some(
      (e) =>
        !e.curatedPlaceOfSafetyId &&
        e.manualPosName?.toLowerCase() === posName.toLowerCase(),
    );

  /** Build the set of incident curated POS IDs for quick lookup. */
  const incidentCuratedPosIds = useMemo(
    () =>
      new Set(
        incidentPlacesOfSafety
          .filter((p) => p.eMHACuratedPlaceOfSafetyId)
          .map((p) => p.eMHACuratedPlaceOfSafetyId!),
      ),
    [incidentPlacesOfSafety],
  );

  /** Unified POS options: merge incident + curated, deduplicate by curated ID. */
  const unifiedOptions = useMemo(() => {
    const optionMap = new Map<string, UnifiedPosOption>();

    // 1. Add curated POS
    for (const pos of curatedPlacesOfSafety) {
      const isOnIncident = incidentCuratedPosIds.has(pos.id);
      const hasExistingForm = isCuratedPosAlreadyUsed(pos.id);
      optionMap.set(pos.id, {
        key: pos.id,
        name: pos.name,
        address: pos.address,
        postalCode: pos.postalCode,
        isOnIncident,
        hasExistingForm,
        curatedPlaceOfSafetyId: pos.id,
        incidentPos: isOnIncident
          ? incidentPlacesOfSafety.find(
              (ip) => ip.eMHACuratedPlaceOfSafetyId === pos.id,
            )
          : undefined,
      });
    }

    // 2. Add non-curated incident POS (manual entries from the incident)
    for (const pos of incidentPlacesOfSafety) {
      if (pos.eMHACuratedPlaceOfSafetyId) continue; // Already handled above
      const hasExistingForm = isManualPosAlreadyUsed(pos.name);
      optionMap.set(`incident-${pos.id}`, {
        key: `incident-${pos.id}`,
        name: pos.name,
        address: pos.address,
        postalCode: pos.postalCode,
        isOnIncident: true,
        hasExistingForm,
        incidentPos: pos,
      });
    }

    // Sort: incident POS first, then alphabetically by name
    return [...optionMap.values()].sort((a, b) => {
      if (a.isOnIncident && !b.isOnIncident) return -1;
      if (!a.isOnIncident && b.isOnIncident) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [
    curatedPlacesOfSafety,
    incidentPlacesOfSafety,
    incidentCuratedPosIds,
    existingOutcomeFormPosEntries,
  ]);

  const isValid = useMemo(() => {
    switch (mode) {
      case "search":
        return selectedPos !== null && !selectedPos.hasExistingForm;
      case "manual":
        return manualName.trim().length > 0;
    }
  }, [mode, selectedPos, manualName]);

  const handleSubmit = () => {
    if (!isValid) return;

    let placeOfSafety: OutcomeFormPlaceOfSafety;

    switch (mode) {
      case "search": {
        if (!selectedPos || selectedPos.hasExistingForm) return;
        if (selectedPos.curatedPlaceOfSafetyId) {
          placeOfSafety = {
            curatedPlaceOfSafetyId: selectedPos.curatedPlaceOfSafetyId,
          };
        } else {
          placeOfSafety = {
            manualPosName: selectedPos.name,
            manualPosAddress: selectedPos.address || undefined,
            manualPosPostalCode: selectedPos.postalCode || undefined,
          };
        }
        break;
      }
      case "manual": {
        if (!manualName.trim()) return;
        placeOfSafety = {
          manualPosName: manualName.trim(),
          manualPosAddress: manualAddress.trim() || undefined,
          manualPosPostalCode: manualPostalCode.trim() || undefined,
          manualPosTrustId: manualTrust?.id ?? undefined,
          manualPosTrustName: manualTrust?.name ?? undefined,
        };
        break;
      }
      default:
        renderErrorToast({ message: "Invalid selection mode" });
        return;
    }

    onSelect(placeOfSafety);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="outcome-form-pos-selection-modal"
    >
      <DialogContent>
        <PopupDialogTitle
          titleText={t(
            "pages.policeIncidentFormPage.outcomeForm.selectPosTitle",
          )}
          subtitleText={t(
            "pages.policeIncidentFormPage.outcomeForm.selectPosSubtitle",
          )}
          closeDialog={onClose}
        />

        {/* Mode toggle: search vs manual */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_e, newMode: PosSelectionMode | null) => {
              if (newMode) setMode(newMode);
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="search">
              {t("pages.policeIncidentFormPage.outcomeForm.posMode.searchList")}
            </ToggleButton>
            <ToggleButton value="manual">
              {t("pages.policeIncidentFormPage.outcomeForm.posMode.manual")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Unified POS typeahead */}
        {mode === "search" && (
          <Autocomplete
            data-testid="unified-pos-select"
            options={unifiedOptions}
            getOptionLabel={(option) => `${option.name}, ${option.address}`}
            getOptionDisabled={(option) => option.hasExistingForm}
            groupBy={(option) =>
              option.isOnIncident
                ? t(
                    "pages.policeIncidentFormPage.outcomeForm.posGroup.onIncident",
                  )
                : t(
                    "pages.policeIncidentFormPage.outcomeForm.posGroup.allLocations",
                  )
            }
            value={selectedPos}
            onChange={(_e, value) => {
              setSelectedPos(value);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.key}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    opacity: option.hasExistingForm ? 0.85 : 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">{option.name}</Typography>
                    {option.hasExistingForm && (
                      <Chip
                        label={t(
                          "pages.policeIncidentFormPage.outcomeForm.posChip.formExists",
                        )}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {[option.address, option.postalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pages.policeIncidentFormPage.outcomeForm.searchPos")}
              />
            )}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            filterOptions={(options, { inputValue }) => {
              const lower = inputValue.toLowerCase();
              return options.filter(
                (o) =>
                  o.name.toLowerCase().includes(lower) ||
                  o.address.toLowerCase().includes(lower) ||
                  o.postalCode.toLowerCase().includes(lower),
              );
            }}
          />
        )}

        {/* Manual entry */}
        {mode === "manual" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              data-testid="manual-pos-name"
              label={t(
                "pages.policeIncidentFormPage.outcomeForm.manualPosName",
              )}
              value={manualName}
              onChange={(e) => {
                setManualName(e.target.value);
              }}
              required
              fullWidth
            />
            <TextField
              data-testid="manual-pos-address"
              label={t(
                "pages.policeIncidentFormPage.outcomeForm.manualPosAddress",
              )}
              value={manualAddress}
              onChange={(e) => {
                setManualAddress(e.target.value);
              }}
              fullWidth
            />
            <TextField
              data-testid="manual-pos-postal-code"
              label={t(
                "pages.policeIncidentFormPage.outcomeForm.manualPosPostalCode",
              )}
              value={manualPostalCode}
              onChange={(e) => {
                setManualPostalCode(e.target.value);
              }}
              fullWidth
            />
            <TrustPicker
              value={manualTrust}
              onChange={(trust: SelectedTrust) => {
                setManualTrust(trust);
              }}
              allowNotListed={true}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          label={t("buttonLabels.cancel")}
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <Button
          label={t("pages.policeIncidentFormPage.outcomeForm.startOutcomeForm")}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          testId="outcome-form-pos-confirm"
        />
      </DialogActions>
    </Dialog>
  );
}
